import { __awaiter } from "tslib";
import { AppHostNovaEventingProvider, useCurrentAppInitialPermissions, } from '@office-start/app-host-nova-eventing';
import { AppHostHubUiService, getHubServices, useAppHostContext, } from '@office-start/app-host-service';
import { AppHostElement, hostVersionsInfo, } from '@office-start/app-host-types';
import { AppHostErrorControl, AppHostLoadingControl, } from '@office-start/app-host-ui-controls';
import { useStandaloneCopilotApp } from '@office-start/copilot-route-service';
import { withLazyErrorBoundary } from '@office-start/error-boundary';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { launchUrl } from '@office-start/launcher-service';
import { copilotM365AppId } from '@office-start/m365-apps-service';
import { DevicePermission, DevicePermissionStorageService, FrameContexts, MetaOsAppContainer, } from '@office-start/metaos-hub-sdk';
import { OfficeWorkload } from '@office-start/officehome-types';
import { handlePauseScheduling } from '@office-start/scheduler-service';
import { format } from '@office-start/string-formatter-utility';
import { updateTaskModule } from '@office-start/task-module-dialog-component';
import { showAboutDialog, showTaskModule, submitTaskModule, } from '@office-start/task-module-dialog-service';
import { logFeature } from '@office-start/telemetry-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import React, { lazy, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useConsentRequest, useMOSCapabilitiesFeatureSettings } from './hooks';
import { formatConsentDialogStrings, generateShareLink, getAgeGroup, getCohort, getStaticTabByEntityIdMap, getUserClassification, isValidUrl, logAppHostError, logAppHostHeaderClickEvent, logAppHostMetrics, } from './utilities';
const LazyAppHostHeaderControl = withLazyErrorBoundary(lazy(() => import(
/* webpackChunkName: "app-host-header-controls"  */ '@office-start/app-host-ui-controls')), 'AppHostHeaderControl');
const LazyCurrentAppPermissionsRequestDialogWithSaveButton = withLazyErrorBoundary(lazy(() => import(
/* webpackChunkName: "current-app-permissions-dialog"  */ '@office-start/device-permissions-controls')), 'CurrentAppPermissionsRequestDialogWithSaveButton');
const DevicePermissionsRequestDialogLazyBoundary = withLazyErrorBoundary(lazy(() => import(
/* webpackChunkName: "device-permissions-request-dialog"  */ '@office-start/device-permissions-controls').then((module) => ({
    default: module.DevicePermissionsRequestDialog,
}))), 'DevicePermissionsRequestDialog');
const ShareLinkDialogComponentLazyBoundary = withLazyErrorBoundary(lazy(() => import(
/* webpackChunkName: "share-link-dialog-component"  */ '@office-start/share-link-dialog-component')), 'ShareLinkDialogComponent');
export const AppHostComponentInternal = ({ appbarConnectorService, appDefinition, appHostSessionId, appId, capabilities, entityId, locStrings, officeComponentContext, officeIdentity, subPageId, additionalParams, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x;
    const firstRenderRef = useRef(true);
    const permissionCapabilitiesRenderRef = useRef(true);
    const hubServicesCreateRef = useRef(null);
    const { metaOSHubResources } = locStrings;
    const featureGates = (_b = (_a = useFeatureGates()) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.result;
    const isDefaultLocStringEnabled = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.defaultLocStringsGates) === null || _c === void 0 ? void 0 : _c.defaultLocStringsEnabled;
    const isPassEligbilityV3ContextEnabled = (_d = featureGates === null || featureGates === void 0 ? void 0 : featureGates.copilotGates) === null || _d === void 0 ? void 0 : _d.copilotPassEligbilityV3ContextEnabled;
    const pauseSchedulerForMosApp = (_f = (_e = featureGates === null || featureGates === void 0 ? void 0 : featureGates.pauseSchedulerGates) === null || _e === void 0 ? void 0 : _e.pauseSchedulerInAHCInternalEnabled) !== null && _f !== void 0 ? _f : false;
    const isStandaloneCopilotApp = useStandaloneCopilotApp();
    if (firstRenderRef.current) {
        logAppHostMetrics({
            appId: appId,
            scenarioName: 'AppHostComponentInternalStart',
            clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
            tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId,
            isStandaloneApp: isStandaloneCopilotApp,
            resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
        });
        firstRenderRef.current = false;
        if (pauseSchedulerForMosApp && appId === copilotM365AppId) {
            appbarConnectorService.setPauseSchedulerInAHCInternal(pauseSchedulerForMosApp);
            logAppHostMetrics({
                appId: appId,
                scenarioName: 'AppHostComponentInternalPauseScheduler',
                clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
                tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
                appUrl: window.location.pathname,
                appHostSessionId: appHostSessionId,
                isStandaloneApp: isStandaloneCopilotApp,
                resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
            });
            handlePauseScheduling();
        }
    }
    // TODO: can we derive/memoize this in the future when in use?
    const [activeSubPageId, setActiveSubPageId] = useState(subPageId);
    const [metaOsAppContainerKey, setMetaOsAppContainerKey] = useState(0);
    const [shareLinkValue, setShareLink] = useState({
        subEntityId: activeSubPageId !== null && activeSubPageId !== void 0 ? activeSubPageId : '',
        subEntityLabel: activeSubPageId !== null && activeSubPageId !== void 0 ? activeSubPageId : '',
    });
    const [showShareLinkDialog, setShowShareLinkDialog] = useState(false);
    const staticTabByEntityIdMap = getStaticTabByEntityIdMap(appDefinition);
    const _initialTabData = staticTabByEntityIdMap.get((_g = entityId === null || entityId === void 0 ? void 0 : entityId.toLowerCase()) !== null && _g !== void 0 ? _g : '');
    const [activeTab, setActiveTab] = useState((_h = _initialTabData === null || _initialTabData === void 0 ? void 0 : _initialTabData.tab) !== null && _h !== void 0 ? _h : appDefinition.staticTabs[0]);
    const [activeTabIndex, setActiveTabIndex] = React.useState((_j = _initialTabData === null || _initialTabData === void 0 ? void 0 : _initialTabData.index) !== null && _j !== void 0 ? _j : 0);
    const { data: userEntitlements } = useUserEntitlements();
    const { ageGroup = null, cohort = null, isCopilotEligible = false, isCodexEnabledRegion = false, isOptedOutByAdmin = false, eduType = null, persona = null, } = ((_k = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result) === null || _k === void 0 ? void 0 : _k.userCopilotEligibilityV3) || {};
    const hostVersionsInfoV2 = useMemo(() => {
        return isPassEligbilityV3ContextEnabled
            ? {
                adaptiveCardSchemaVersion: hostVersionsInfo.adaptiveCardSchemaVersion,
                appEligibilityInformation: {
                    ageGroup: getAgeGroup(ageGroup),
                    cohort: getCohort(cohort),
                    isCopilotEligible: !!isCopilotEligible,
                    isCopilotEnabledRegion: !!isCodexEnabledRegion,
                    isOptedOutByAdmin: !!isOptedOutByAdmin,
                    userClassification: getUserClassification(eduType, persona),
                },
            }
            : hostVersionsInfo;
    }, [
        ageGroup,
        cohort,
        eduType,
        isCodexEnabledRegion,
        isCopilotEligible,
        isOptedOutByAdmin,
        isPassEligbilityV3ContextEnabled,
        persona,
    ]);
    /* Initial value comes from appbar if app launch is from app bar,
    or use current datetime if app launches from refresh while app is running */
    const [userClickTime, setUserClickTime] = useState((appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime) || Date.now());
    const refreshOrRetryCallback = useCallback((telemetryElement, actionTarget) => {
        setUserClickTime(Date.now());
        setMetaOsAppContainerKey((mosAppContainerKey) => mosAppContainerKey + 1);
        logAppHostHeaderClickEvent('Retry', appId, telemetryElement, actionTarget, '0');
    }, [appId]);
    // These two wrappers distinguish whether the refresh happened in the Error or App Header UI
    const refreshCallback = useCallback(() => refreshOrRetryCallback(AppHostElement.Refresh, 'MOSHeaderMenuItem'), [refreshOrRetryCallback]);
    const [hubServices, setHubServices] = useState();
    const retryCallback = useCallback(() => refreshOrRetryCallback(AppHostElement.Retry, 'MOSRetryButton'), [refreshOrRetryCallback]);
    const setFrameInfoCallback = useCallback((frameInfo) => {
        logFeature({
            featureName: "MOSHub" /* KnownFeatureNames.MOSHub */,
            eventName: 'SetCurrentFrame',
            params: {
                AppId: appId,
                AppCapabilities: 'setFrameInfo',
                DebugInfo: `setFrameInfoCallback `,
                Service: 'AppHostPageService',
            },
        });
        if (!(isValidUrl(frameInfo.contentUrl) ||
            isValidUrl(frameInfo.websiteUrl))) {
            logAppHostError('Invalid_FrameInfo_Url');
        }
        setActiveTab(Object.assign(Object.assign({}, activeTab), { websiteUrl: frameInfo.websiteUrl, contentUrl: frameInfo.contentUrl }));
    }, [activeTab, appId]);
    const toggleShareLinkDialogVisibility = useCallback(() => setShowShareLinkDialog(false), []);
    const openBrowserCallback = useCallback(() => {
        if (activeTab.websiteUrl) {
            const linkTarget = '_blank';
            const action = 'OpenInBrowser';
            launchUrl(activeTab.websiteUrl, { action, linkTarget });
        }
        logAppHostHeaderClickEvent('Navigate', appId, AppHostElement.OpenBrowser, 'MOSHeaderMenuItem', '2');
    }, [activeTab.websiteUrl, appId]);
    const onClickTabCallback = useCallback((tab, index) => {
        setUserClickTime(Date.now());
        setActiveTab(tab);
        setActiveTabIndex(index);
        setActiveSubPageId(undefined);
        logAppHostHeaderClickEvent('Navigate', appId, AppHostElement.Tab, 'Tab', index.toString());
    }, [appId]);
    const capabilitiesFeatureSettings = useMOSCapabilitiesFeatureSettings();
    // TODO: results not needed will remove when function signature changes
    const submitTask = useCallback((_results) => {
        submitTaskModule();
    }, []);
    const updateTask = useCallback((dialogSize) => {
        updateTaskModule(dialogSize);
    }, []);
    const startTask = useCallback((dialogInfo) => {
        showTaskModule({
            appId,
            appHostSessionId,
            taskInfo: dialogInfo,
            dialogType: 'TASKMODULE',
        });
    }, [appHostSessionId, appId]);
    const setShareLinkCallback = useCallback((params) => {
        logFeature({
            featureName: "MOSHub" /* KnownFeatureNames.MOSHub */,
            eventName: 'ShareDeepLink',
            params: {
                AppId: appId,
                AppCapabilities: 'ShareLink',
                DebugInfo: `SubEntityId: ${params.subEntityId}, SubEntityLabel: ${params.subEntityLabel}, WebsiteUrl: ${params.subEntityWebUrl}`,
                Service: 'AppHostPageService',
            },
        });
        setShareLink(params);
        setShowShareLinkDialog(true);
    }, [appId]);
    const { permissionRequested, showConsentDialogCallback, closeConsentDialogCallback, } = useConsentRequest(appId);
    const [isHubServicesCreated, setIsHubServicesCreated] = useState(false);
    useEffect(() => {
        let isMounted = true;
        const fetchHubServices = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const createdHubServices = yield getHubServices({
                    appId,
                    appHostSessionId,
                    frameContext: FrameContexts.content,
                    appbarConnectorService,
                    dialogHandlers: {
                        startTask,
                        submitTask,
                        updateTask,
                    },
                    retryCallback,
                    setFrameInfoCallback,
                    setShareLinkCallback,
                    showConsentDialogCallback,
                    userId: officeIdentity.puid,
                    appInfo: appHostContext === null || appHostContext === void 0 ? void 0 : appHostContext.app,
                    userInfo: appHostContext
                        ? Object.assign({ id: officeIdentity.puid }, appHostContext === null || appHostContext === void 0 ? void 0 : appHostContext.user) : undefined,
                    defaultLocStrings: isDefaultLocStringEnabled
                        ? locStrings
                        : undefined,
                    isStandaloneCopilotApp,
                    featureGates,
                });
                if (isMounted && createdHubServices) {
                    // TODO will remove extra logging once verified the edge cases
                    if (appId != hubServicesCreateRef.current) {
                        logAppHostMetrics({
                            appId: appId,
                            scenarioName: 'AppHostHubServicesReady',
                            clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
                            tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
                            appUrl: window.location.pathname,
                            appHostSessionId: appHostSessionId,
                            isStandaloneApp: isStandaloneCopilotApp,
                            resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
                        });
                    }
                    setHubServices(createdHubServices);
                    setIsHubServicesCreated(true);
                }
                // Log time taken to fetch hub services
                if (appId != hubServicesCreateRef.current) {
                    logAppHostMetrics({
                        appId: appId,
                        scenarioName: 'AppHostHubServicesInit',
                        clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
                        tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
                        appUrl: window.location.pathname,
                        appHostSessionId: appHostSessionId,
                        isStandaloneApp: isStandaloneCopilotApp,
                        resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
                    });
                    appbarConnectorService.setStageBEndTime(Date.now());
                    // Extra logging if hub services creation failed
                    if (!createdHubServices) {
                        logAppHostMetrics({
                            appId: appId,
                            scenarioName: 'AppHostHubServicesUndefined',
                            clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
                            tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
                            appUrl: window.location.pathname,
                            appHostSessionId: appHostSessionId,
                            isStandaloneApp: isStandaloneCopilotApp,
                            resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
                        });
                    }
                    hubServicesCreateRef.current = appId;
                }
            }
            catch (error) {
                // Log the error
                logAppHostError('AppHostHubServices_Error', error.toString());
            }
        });
        fetchHubServices();
        return () => {
            isMounted = false;
        };
    }, [
        appHostSessionId,
        appId,
        appbarConnectorService,
        window.location.pathname,
        officeIdentity.puid,
        retryCallback,
        setFrameInfoCallback,
        setShareLinkCallback,
        showConsentDialogCallback,
        startTask,
        submitTask,
        updateTask,
    ]);
    useEffect(() => {
        if (isDefaultLocStringEnabled) {
            if (isHubServicesCreated && hubServices) {
                const hubUiService = new AppHostHubUiService(officeComponentContext.correlationId, retryCallback, metaOSHubResources, hubServices.telemetryService, appId);
                setHubServices(Object.assign(Object.assign({}, hubServices), { hubUiService }));
            }
        }
    }, [
        metaOSHubResources,
        isDefaultLocStringEnabled,
        officeComponentContext.correlationId,
        retryCallback,
        appId,
        isHubServicesCreated,
    ]);
    const aunthCallbackHandler = useCallback((event) => {
        try {
            // Gate authcallback behind m365TestAppEnabled during e2e testing
            // TODO: remove this check after testing
            // TODO: add officeComponentContext.worklaod === OfficeWorkload.Hwa check after testing
            if (!event ||
                !event.data ||
                officeComponentContext.workload !== OfficeWorkload.Hwa) {
                return;
            }
            const data = event.data;
            if (data &&
                data.type === 'authcallback' &&
                data.appId === appId &&
                (hubServices === null || hubServices === void 0 ? void 0 : hubServices.authenticationService)) {
                hubServices.authenticationService.dispatchEvent('onExternalAuthDeeplink', {
                    data: {
                        authId: data.authId,
                        result: data.result,
                    },
                });
            }
        }
        catch (e) {
            logAppHostError('M365Apps_AuthCallback_Error', e.toString());
        }
    }, [
        appId,
        hubServices === null || hubServices === void 0 ? void 0 : hubServices.authenticationService,
        officeComponentContext.workload,
    ]);
    useEffect(() => {
        window.addEventListener('message', aunthCallbackHandler);
        return () => {
            if (aunthCallbackHandler) {
                window.removeEventListener('message', aunthCallbackHandler);
            }
        };
    }, [aunthCallbackHandler]);
    const openAboutCallback = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        showAboutDialog(appId);
        logAppHostHeaderClickEvent('Open', appId, AppHostElement.About, 'MOSHeaderMenuItem', '1');
    }), [appId]);
    const [showAppPermissionsDialog, setShowAppPermissionsDialog] = useState(false);
    const openAppPermissionsCallback = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setShowAppPermissionsDialog(true);
        logAppHostHeaderClickEvent('Open', appId, AppHostElement.AppPermissions, 'MOSHeaderMenuItem', '3');
    }), [appId]);
    //extract out properties named different from hubSDK IAppDefinitionType for renaming
    const containerAppDefinition = useMemo(() => {
        return appDefinition.hubSdkAppDefinition;
    }, [appDefinition]);
    const formattedConsentDialogStrings = useMemo(() => formatConsentDialogStrings(appDefinition.name, locStrings.devicePermissionDialogResources), [appDefinition.name, locStrings.devicePermissionDialogResources]);
    const appHostContext = useAppHostContext({
        appHostSessionId,
        appId,
        entityId: activeTab.entityId,
        frameContext: FrameContexts.content,
        subPageId: activeSubPageId,
        isFullScreen: appDefinition.isFullScreen,
        iconPositionVertical: (_m = (_l = appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.getAppIconPositionRect) === null || _l === void 0 ? void 0 : _l.call(appbarConnectorService)) === null || _m === void 0 ? void 0 : _m.top,
        userClickTime: userClickTime,
        ringId: capabilitiesFeatureSettings.ringId,
        additionalParams,
    });
    const devicePermissionStorageService = useMemo(() => {
        var _a;
        if (hubServices === undefined ||
            (hubServices === null || hubServices === void 0 ? void 0 : hubServices.devicePermissionService) === undefined ||
            ((_a = hubServices === null || hubServices === void 0 ? void 0 : hubServices.devicePermissionService) === null || _a === void 0 ? void 0 : _a.storageService) === undefined) {
            return new DevicePermissionStorageService(appId, officeIdentity.puid);
        }
        return hubServices.devicePermissionService.storageService;
    }, [appId, officeIdentity.puid, hubServices]);
    const isAppHostPermissionsEnabled = (_p = (_o = featureGates === null || featureGates === void 0 ? void 0 : featureGates.appHostPermissionsGates) === null || _o === void 0 ? void 0 : _o.appHostPermissionsEnabled) !== null && _p !== void 0 ? _p : true;
    const isAppHostPermissionsNeeded = useMemo(() => {
        const isCopilotApp = appId === copilotM365AppId;
        return isAppHostPermissionsEnabled || !isCopilotApp;
    }, [isAppHostPermissionsEnabled, appId]);
    const [devicePermissionsCapabilityItems, setDevicePermissionsCapabilityItems,] = useCurrentAppInitialPermissions({
        appDefinitionPermissions: appDefinition.devicePermissions,
        devicePermissionsStorage: devicePermissionStorageService,
        appHostPermissionsEnabled: isAppHostPermissionsNeeded,
    });
    if (permissionCapabilitiesRenderRef.current &&
        devicePermissionsCapabilityItems &&
        devicePermissionsCapabilityItems.length > 0) {
        logAppHostMetrics({
            appId: appId,
            scenarioName: 'AppHostComponentInternalPermissions',
            clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
            tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId,
            isStandaloneApp: isStandaloneCopilotApp,
            resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
        });
        permissionCapabilitiesRenderRef.current = false;
    }
    if (permissionCapabilitiesRenderRef.current &&
        !isAppHostPermissionsEnabled) {
        logAppHostMetrics({
            appId: appId,
            scenarioName: 'AppHostComponentInternalPermissions',
            clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
            tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId,
            isStandaloneApp: isStandaloneCopilotApp,
            resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
        });
        permissionCapabilitiesRenderRef.current = false;
    }
    const closeAppPermissionsDialog = useCallback(() => setShowAppPermissionsDialog(false), []);
    const isSpfx = activeTab.contentUrl &&
        ((_q = activeTab.contentUrl) === null || _q === void 0 ? void 0 : _q.indexOf('teamSiteDomain')) > 0;
    const isMySiteDomainNeeded = activeTab.contentUrl &&
        ((_r = activeTab.contentUrl) === null || _r === void 0 ? void 0 : _r.indexOf('mySiteDomain')) > 0;
    const isMySitePathNeeded = activeTab.contentUrl && ((_s = activeTab.contentUrl) === null || _s === void 0 ? void 0 : _s.indexOf('mySitePath')) > 0;
    const isNavigationSourceNeeded = activeTab.contentUrl &&
        ((_t = activeTab.contentUrl) === null || _t === void 0 ? void 0 : _t.indexOf('navigationSource')) > 0;
    if (isNavigationSourceNeeded) {
        let source = 'direct';
        if (appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged) {
            source =
                appbarConnectorService.appUrl === '/apps/'
                    ? 'appGallery'
                    : ((_u = featureGates === null || featureGates === void 0 ? void 0 : featureGates.myContentGates) === null || _u === void 0 ? void 0 : _u.myContentReplacementEnabled)
                        ? 'defaultPinned'
                        : 'manuallyPinned';
        }
        else {
            source = 'direct';
        }
        activeTab.contentUrl = (_v = activeTab.contentUrl) === null || _v === void 0 ? void 0 : _v.replace('{source}', source);
    }
    useEffect(() => {
        var _a;
        if (isMySitePathNeeded && !((_a = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result) === null || _a === void 0 ? void 0 : _a.OneDriveUrl)) {
            logAppHostError('MySitePath_Error', 'Missing MySitePath/OneDriveUrl resource Id');
        }
    }, [isMySitePathNeeded, (_w = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result) === null || _w === void 0 ? void 0 : _w.OneDriveUrl]);
    if (!activeTab.contentUrl) {
        logAppHostError('Missing_ContentUrl_Error');
        return (React.createElement(AppHostErrorControl, { correlationId: officeComponentContext.correlationId, refreshCallBack: retryCallback, errorStrings: {
                errorUiHeaderText: metaOSHubResources.errorUiTitle,
                errorUiSecondaryText: metaOSHubResources.errorUiLabel,
                correlationIdLabel: metaOSHubResources.correlationId,
                retryButtonText: metaOSHubResources.retryButtonTitle,
            } }));
    }
    if (!hubServices || !appHostContext) {
        return (React.createElement(AppHostLoadingControl, { label: metaOSHubResources.loadingLabel }));
    }
    if ((isSpfx || isMySiteDomainNeeded) &&
        !((_x = userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result) === null || _x === void 0 ? void 0 : _x.SharePointResourceId)) {
        logAppHostError('SharePointResourceIdMissing_Error', 'Missing sharepoint resource Id');
        if (isSpfx) {
            return (React.createElement(AppHostErrorControl, { correlationId: officeComponentContext.correlationId, refreshCallBack: retryCallback, errorStrings: {
                    errorUiHeaderText: metaOSHubResources.errorUiTitle,
                    errorUiSecondaryText: metaOSHubResources.errorUiLabel,
                    correlationIdLabel: metaOSHubResources.correlationId,
                    retryButtonText: metaOSHubResources.retryButtonTitle,
                } }));
        }
    }
    const showHeader = !appDefinition.isFullScreen;
    //TODO URL format is temporary and will be changed.
    const shareLinkUrl = generateShareLink(shareLinkValue, appDefinition.id, activeTab.entityId);
    // if hubServices.nestedAppAuthService is available, recommend the metaos app to use NAA channel
    const isNAAChannelRecommended = !!hubServices.nestedAppAuthService;
    logAppHostMetrics({
        appId: appId,
        scenarioName: 'AppHostComponentInternalRender',
        clientTimeStamp: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.userClickTime,
        tileChanged: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged,
        appUrl: window.location.pathname,
        isStandaloneApp: isStandaloneCopilotApp,
        resourceDeliveryType: appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.resourceDeliveryType,
    });
    const renderLazyAppHostHeaderControl = () => (React.createElement(LazyAppHostHeaderControl, { activeTabIndex: activeTabIndex, appDefinition: appDefinition, setActiveTabCallback: onClickTabCallback, refreshCallback: refreshCallback, locStrings: locStrings.metaOSHubResources, openBrowserCallback: activeTab.websiteUrl ? openBrowserCallback : undefined, openAboutCallback: (capabilities === null || capabilities === void 0 ? void 0 : capabilities.m365AppsAboutEnabled)
            ? openAboutCallback
            : undefined, openAppPermissionsCallback: openAppPermissionsCallback }));
    const renderCurrentAppPermissionsRequestDialogWithSaveButton = () => (React.createElement(LazyCurrentAppPermissionsRequestDialogWithSaveButton, { appId: containerAppDefinition.id, includeNavigateToInstalledAppsControlText: false }));
    return (React.createElement(AppHostNovaEventingProvider, { hideRequestDialog: closeConsentDialogCallback, devicePermissionDialogResources: formattedConsentDialogStrings, appDefinition: appDefinition, metaOSHubResources: metaOSHubResources, currentAppDefaultPermissions: devicePermissionsCapabilityItems, closeAppPermissionsDialog: closeAppPermissionsDialog, devicePermissionStorageService: devicePermissionStorageService, setDevicePermissionsCapabilityItems: setDevicePermissionsCapabilityItems },
        showHeader && renderLazyAppHostHeaderControl(),
        showHeader &&
            showAppPermissionsDialog &&
            renderCurrentAppPermissionsRequestDialogWithSaveButton(),
        React.createElement(MetaOsAppContainer, { key: metaOsAppContainerKey, appDefinition: containerAppDefinition, contentUrl: activeTab.contentUrl, context: appHostContext, hostWindow: window, hubServices: hubServices, title: appDefinition.name, hostVersionsInfo: hostVersionsInfoV2, isNAAChannelRecommended: isNAAChannelRecommended }),
        showShareLinkDialog && (React.createElement(ShareLinkDialogComponentLazyBoundary, { link: shareLinkUrl, linkText: shareLinkValue.subEntityLabel, dialogTitle: format(metaOSHubResources.linkDialogTitle, shareLinkValue.subEntityLabel), onClose: toggleShareLinkDialogVisibility })),
        !!permissionRequested && (React.createElement(DevicePermissionsRequestDialogLazyBoundary, { appId: appId, permissionBeingRequested: DevicePermission[permissionRequested] }))));
};
//# sourceMappingURL=AppHostComponentInternal.js.map